import { PROVINCES } from 'utils/constants';
import { ErrorData } from 'types/interfaces';
import { AcmEligibilityResponse, EligibilityRuleFieldMap } from '../types';
import { ACCOUNT_TYPE } from 'containers/AccountsStep1/constants';
import { getTextFactory } from 'utils/TextUtils';

const RULE_CODE_BR0002 = 'BR0002';
const BR0002_LIF_PROVINCES = [PROVINCES.AB, PROVINCES.BC, PROVINCES.NB];
const ACCOUNT_ELIGIBILITY_ERROR_PREFIX = 'accountEligibility.';

const getProvinceCode = (legislationProvince?: string) => legislationProvince?.replace('CA-', '') ?? '';

const isLifInBR0002Province = (response: AcmEligibilityResponse) =>
  response.account.accountType === ACCOUNT_TYPE.LIF &&
  !!response.account.legislationProvince &&
  BR0002_LIF_PROVINCES.includes(response.account.legislationProvince as PROVINCES);

export const getAccountLabel = (
  account: AcmEligibilityResponse['account'],
  getGlobalText: ReturnType<typeof getTextFactory>,
) => {
  const fullAccountTypeLabel = getGlobalText(`accountType.${account.accountType}`) || account.accountType;
  const accountTypeLabel = fullAccountTypeLabel.split(' (')[0];
  const provinceCode = getProvinceCode(account.legislationProvince);
  const currencyLabel = getGlobalText(`currency.${account.currency}`) || account.currency;
  const parts = [accountTypeLabel, provinceCode, currencyLabel].filter(Boolean);
  return parts.join(' ');
};

export const getRuleMessageKey = (ruleCode: string, acmEligibilityResponses: AcmEligibilityResponse[]) => {
  if (ruleCode === RULE_CODE_BR0002) return getBR0002MessageKey(acmEligibilityResponses);
  return ruleCode;
};

export const getBR0002Responses = (responses: AcmEligibilityResponse[]): AcmEligibilityResponse[] => {
  return responses.filter((r) => r.failedRules.includes(RULE_CODE_BR0002));
};

export const getBR0002MessageKey = (responses: AcmEligibilityResponse[]): string => {
  const br0002Responses = getBR0002Responses(responses);

  const lifLegislationProvinces = new Set(
    br0002Responses.filter((r) => r.account.accountType === ACCOUNT_TYPE.LIF).map((r) => r.account.legislationProvince),
  );

  const hasLIF_AB = lifLegislationProvinces.has(PROVINCES.AB);
  const hasLIF_BC = lifLegislationProvinces.has(PROVINCES.BC);
  const hasLIF_NB = lifLegislationProvinces.has(PROVINCES.NB);

  const hasNonLifOrOtherProvinceCase = br0002Responses.some((response) => !isLifInBR0002Province(response));

  const suffixRules: Array<{ when: boolean; suffix: string }> = [
    { when: hasLIF_AB && hasNonLifOrOtherProvinceCase, suffix: 'mixedAB' },
    { when: hasLIF_BC && hasNonLifOrOtherProvinceCase, suffix: 'mixedBC' },
    { when: hasLIF_NB && hasNonLifOrOtherProvinceCase, suffix: 'mixedNB' },
    { when: hasLIF_AB || hasLIF_BC, suffix: 'onlyABorBC' },
    { when: hasLIF_NB, suffix: 'onlyNB' },
  ];

  const matchedRule = suffixRules.find((rule) => rule.when);
  if (matchedRule) return `${RULE_CODE_BR0002}.${matchedRule.suffix}`;
  return RULE_CODE_BR0002;
};

const getRuleFieldNames = <F extends string>(
  ruleCode: string,
  ruleFieldMap?: EligibilityRuleFieldMap<F>,
): readonly F[] => {
  const mappedFields = ruleFieldMap?.[ruleCode];
  if (!mappedFields) return [];
  if (typeof mappedFields === 'string') return [mappedFields];
  return mappedFields;
};

export const getEligibilityFieldError = <F extends string>(
  responses: AcmEligibilityResponse[] | undefined,
  ruleFieldMap: EligibilityRuleFieldMap<F>,
  fieldName: F,
): ErrorData | undefined => {
  if (!responses?.length) return undefined;

  for (const { failedRules } of responses) {
    for (const ruleCode of failedRules) {
      if (getRuleFieldNames(ruleCode, ruleFieldMap).includes(fieldName)) {
        return { type: `${ACCOUNT_ELIGIBILITY_ERROR_PREFIX}${getRuleMessageKey(ruleCode, responses)}` };
      }
    }
  }

  return undefined;
};
